import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Download, FileJson, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import { kinds, type EnvKey } from '@/app/site/scope';
import { schemaExport, schemaImport } from '@/shared/api/generated/content/content';
import type { ImportOutputBody, SchemaFile } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { toast } from '@/shared/ui/Toast';

import styles from './SchemaMenu.module.scss';

const KIND: Record<string, string> = {
  collection: 'коллекция',
  single: 'одна запись',
  block: 'компонент',
};

/**
 * Схема файлом (шаг 7.7): выгрузить модели, компоненты, поля, статусы и
 * правила без записей — и загрузить такой файл в любой сайт. Загрузка
 * сначала показывает, что заведётся, а что пропустится.
 */
export function SchemaMenu({
  siteId,
  env,
  siteSlug,
}: {
  siteId: string;
  env: EnvKey;
  siteSlug: string;
}) {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<SchemaFile>();
  const [plan, setPlan] = useState<ImportOutputBody>();
  const [busy, setBusy] = useState(false);

  const download = () => {
    schemaExport(siteId, kinds[env])
      .then((response) => {
        const body = response.data as SchemaFile & { $schema?: string };
        const { $schema: _drop, ...clean } = body;
        const blob = new Blob([JSON.stringify(clean, null, 2)], { type: 'application/json' });
        const link = document.createElement('a');

        link.href = URL.createObjectURL(blob);
        link.download = `schema-${siteSlug}-${env}.json`;
        link.click();
        URL.revokeObjectURL(link.href);
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось выгрузить');
      });
  };

  const pick = async (picked: File) => {
    let parsed: SchemaFile;

    try {
      parsed = JSON.parse(await picked.text()) as SchemaFile;
    } catch {
      toast.error('Это не JSON-файл схемы');

      return;
    }

    setBusy(true);

    schemaImport(siteId, kinds[env], parsed, { dry_run: true })
      .then((response) => {
        setFile(parsed);
        setPlan(response.data as ImportOutputBody);
      })
      .catch((cause: unknown) => {
        toast.error('Файл не принят', {
          description:
            cause instanceof ApiError ? cause.detail : 'Проверьте, что это файл схемы Faceta.',
        });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const run = () => {
    if (file === undefined) {
      return;
    }

    setBusy(true);

    schemaImport(siteId, kinds[env], file)
      .then((response) => {
        const done = response.data as ImportOutputBody;

        toast.success(`Заведено моделей: ${String(done.created?.length ?? 0)}`);
        setPlan(undefined);
        setFile(undefined);
        void queryClient.invalidateQueries({ queryKey: ['content-types', siteId] });
      })
      .catch((cause: unknown) => {
        toast.error('Не загрузилось', {
          description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
        });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const created = plan?.created ?? [];
  const skipped = plan?.skipped ?? [];

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" tone="neutral" iconLeft={<FileJson size={15} />} loading={busy}>
            Схема <ChevronDown size={14} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem icon={<Download size={15} />} onSelect={download}>
            Выгрузить JSON
          </DropdownMenuItem>
          <DropdownMenuItem
            icon={<Upload size={15} />}
            onSelect={() => {
              input.current?.click();
            }}
          >
            Загрузить JSON…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <input
        ref={input}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={(event) => {
          const picked = event.target.files?.[0];
          event.target.value = '';

          if (picked !== undefined) {
            void pick(picked);
          }
        }}
      />

      <Dialog
        open={plan !== undefined}
        onOpenChange={(next) => {
          if (!next) {
            setPlan(undefined);
          }
        }}
      >
        {plan !== undefined && (
          <DialogContent
            title="Загрузить схему"
            description={`В ${env === 'dev' ? 'Development' : 'Production'} этого сайта. Только добавляет — существующие модели не меняются.`}
            size="md"
            footer={
              <Button loading={busy} disabled={created.length === 0} onClick={run}>
                {created.length === 0 ? 'Заводить нечего' : `Завести ${String(created.length)}`}
              </Button>
            }
          >
            {created.length > 0 && (
              <section className={styles['block']}>
                <h3 className={styles['title']}>Заведётся</h3>
                <ul className={styles['list']}>
                  {created.map((item) => (
                    <li key={item.slug}>
                      {item.name}{' '}
                      <span className={styles['muted']}>
                        · {item.slug} · {KIND[item.kind] ?? item.kind}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {skipped.length > 0 && (
              <section className={styles['block']}>
                <h3 className={styles['title']}>Пропустится</h3>
                <ul className={styles['list']}>
                  {skipped.map((item) => (
                    <li key={item.slug}>
                      {item.name}{' '}
                      <span className={styles['muted']}>
                        · {item.slug} — {item.reason}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}
